import React, { useState, useEffect } from 'react';
import { BarChart2, Clock, TrendingUp, ArrowLeftRight, Loader2, Users, MessageCircle } from 'lucide-react';
import { advancedReportsAPI } from '../../services/api';
import { Alert, AlertDescription } from '../ui/alert';
import { Progress } from '../ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

interface ConversationAnalyticsProps {
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
  selectedAgent: number | 'all';
  selectedChannel: number | 'all';
}

export const ConversationAnalytics: React.FC<ConversationAnalyticsProps> = ({
  dateRange,
  selectedAgent,
  selectedChannel,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [analyticsData, setAnalyticsData] = useState<any>(null);

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange, selectedAgent, selectedChannel]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        start_date: dateRange.startDate.toISOString().split('T')[0],
        end_date:   dateRange.endDate.toISOString().split('T')[0],
      };
      const data = await advancedReportsAPI.getConversationalAnalytics(params);
      setAnalyticsData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch conversation analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="m-2">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!analyticsData) {
    return (
      <Alert className="m-2">
        <AlertDescription>No analytics data available for the selected period.</AlertDescription>
      </Alert>
    );
  }

  // Prepare data for export — untouched
  const exportData = [
    {
      total_conversations:      analyticsData.total_conversations,
      avg_duration:             analyticsData.avg_conversation_duration,
      messages_per_conversation: analyticsData.messages_per_conversation,
      handoff_rate:             analyticsData.handoff_rate,
    },
    ...(Array.isArray(analyticsData.peak_hours)         ? analyticsData.peak_hours         : []),
    ...(Array.isArray(analyticsData.resolution_patterns) ? analyticsData.resolution_patterns : []),
  ];

  const handoffRate =
    analyticsData.handoff_analysis?.with_handoff && analyticsData.handoff_analysis?.without_handoff
      ? (
          (analyticsData.handoff_analysis.with_handoff /
            (analyticsData.handoff_analysis.with_handoff +
              analyticsData.handoff_analysis.without_handoff)) *
          100
        ).toFixed(1)
      : '0.0';

  // Max count for peak-hours bar scaling
  const peakMax = analyticsData.peak_hours?.length
    ? Math.max(...analyticsData.peak_hours.map((h: any) => h.count || 0))
    : 1;

  // Max count for resolution-patterns bar scaling
  const resolutionEntries = Object.entries(analyticsData.resolution_patterns) as [string, number][];
  const resolutionMax = resolutionEntries.length
    ? Math.max(...resolutionEntries.map(([, v]) => v as number))
    : 1;

  return (
    <div className="space-y-5">

      {/* ── Top stat strip ───────────────────────────────────────────────── */}
      <div className="flex flex-wrap divide-x divide-border rounded-lg border border-border/60 bg-muted/10 overflow-hidden">
        <div className="flex flex-col gap-0.5 px-4 py-3 flex-1 min-w-[130px]">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <BarChart2 className="h-3.5 w-3.5" />Total Conversations
          </span>
          <span className="text-lg font-semibold tabular-nums mt-1">
            {analyticsData.total_conversations.toLocaleString()}
          </span>
        </div>

        <div className="flex flex-col gap-0.5 px-4 py-3 flex-1 min-w-[130px]">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />Avg Conv Length
          </span>
          <span className="text-lg font-semibold tabular-nums mt-1">
            {analyticsData.avg_conversation_length?.toFixed(1) || '0.0'}
            <span className="text-sm font-normal text-muted-foreground ml-0.5">h</span>
          </span>
        </div>

        <div className="flex flex-col gap-0.5 px-4 py-3 flex-1 min-w-[130px]">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <TrendingUp className="h-3.5 w-3.5" />Avg Messages / Conv
          </span>
          <span className="text-lg font-semibold tabular-nums mt-1">
            {analyticsData.avg_messages_per_conversation?.toFixed(1) || '0.0'}
          </span>
        </div>

        <div className="flex flex-col gap-0.5 px-4 py-3 flex-1 min-w-[130px]">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ArrowLeftRight className="h-3.5 w-3.5" />Handoff Rate
          </span>
          <span className="text-lg font-semibold tabular-nums mt-1">
            {handoffRate}
            <span className="text-sm font-normal text-muted-foreground ml-0.5">%</span>
          </span>
        </div>
      </div>

      {/* ── Peak Hours + Resolution Patterns ─────────────────────────────── */}
      <div className="grid gap-4 md:grid-cols-[3fr_2fr]">

        {/* Peak Hours — table with inline progress bar for visual % weight */}
        <div className="rounded-lg border border-border/60 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/20">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-sm font-medium">Peak Hours Analysis</span>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-16">Hour</TableHead>
                <TableHead className="text-right w-24">Conversations</TableHead>
                <TableHead>% of Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {analyticsData.peak_hours?.length > 0
                ? analyticsData.peak_hours.map((hour: any, index: number) => {
                    const pct = analyticsData.total_conversations && hour.count
                      ? (hour.count / analyticsData.total_conversations) * 100
                      : 0;
                    const barPct = peakMax > 0 ? ((hour.count || 0) / peakMax) * 100 : 0;
                    return (
                      <TableRow key={index}>
                        <TableCell className="text-sm font-medium tabular-nums">
                          {String(hour.hour).padStart(2, '0')}:00
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                          {hour.count || 0}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={barPct} className="h-1.5 flex-1" />
                            <span className="text-xs tabular-nums text-muted-foreground w-10 text-right shrink-0">
                              {pct.toFixed(1)}%
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                : (
                  <TableRow>
                    <TableCell colSpan={3} className="py-8 text-center text-sm text-muted-foreground">
                      No peak hours data for this period
                    </TableCell>
                  </TableRow>
                )}
            </TableBody>
          </Table>
        </div>

        {/* Resolution Patterns — compact rows with visual bar */}
        <div className="rounded-lg border border-border/60 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/20">
            <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-sm font-medium">Resolution Patterns</span>
          </div>
          <div className="divide-y divide-border/40">
            {resolutionEntries.length > 0
              ? resolutionEntries.map(([pattern, count]) => {
                  const barPct = resolutionMax > 0 ? ((count as number) / resolutionMax) * 100 : 0;
                  return (
                    <div key={pattern} className="flex flex-col gap-1.5 px-4 py-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm capitalize text-muted-foreground">
                          {pattern.replace(/_/g, ' ')}
                        </span>
                        <span className="text-sm font-semibold tabular-nums">{count as number}</span>
                      </div>
                      <Progress value={barPct} className="h-1" />
                    </div>
                  );
                })
              : (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  No resolution data available
                </div>
              )}
          </div>
        </div>

      </div>

      {/* ── Handoff + Message Flow — collapsed into one compact strip ─────
           4 single-number data points don't deserve 2 separate sections. */}
      <div className="flex flex-wrap divide-x divide-border rounded-lg border border-border/60 bg-muted/10 overflow-hidden">

        <div className="flex flex-col gap-0.5 px-4 py-3 flex-1 min-w-[130px]">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ArrowLeftRight className="h-3.5 w-3.5" />With Handoff
          </span>
          <span className="text-lg font-semibold tabular-nums mt-1">
            {analyticsData.handoff_analysis?.with_handoff ?? '—'}
          </span>
        </div>

        <div className="flex flex-col gap-0.5 px-4 py-3 flex-1 min-w-[130px]">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Users className="h-3.5 w-3.5" />Single Agent
          </span>
          <span className="text-lg font-semibold tabular-nums mt-1">
            {analyticsData.handoff_analysis?.without_handoff ?? '—'}
          </span>
        </div>

        <div className="flex flex-col gap-0.5 px-4 py-3 flex-1 min-w-[130px]">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <MessageCircle className="h-3.5 w-3.5" />Avg Customer Msgs
          </span>
          <span className="text-lg font-semibold tabular-nums mt-1">
            {analyticsData.conversation_flow_analysis?.avg_customer_messages?.toFixed(1) || '0.0'}
          </span>
        </div>

        <div className="flex flex-col gap-0.5 px-4 py-3 flex-1 min-w-[130px]">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <MessageCircle className="h-3.5 w-3.5" />Avg Agent Msgs
          </span>
          <span className="text-lg font-semibold tabular-nums mt-1">
            {analyticsData.conversation_flow_analysis?.avg_agent_messages?.toFixed(1) || '0.0'}
          </span>
        </div>

      </div>
    </div>
  );
};
